import { useRef, useEffect, useState } from 'react'
import * as maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import { useVisitorMap } from '@/hooks/useVisitorMap'
import { useTheme } from '@/contexts/ThemeContext'
import type { Visitor, PresenceStats } from './types'

interface VisitorMapProps {
  visitors: Visitor[]
  pmtilesUrl: string
  selectedId: string | null
  hoveredId: string | null
  onVisitorSelect: (id: string) => void
  onMapReady: (map: maplibregl.Map) => void
  presenceStats: PresenceStats
}

export function VisitorMap({
  visitors,
  pmtilesUrl,
  selectedId,
  hoveredId,
  onVisitorSelect,
  onMapReady,
  presenceStats,
}: VisitorMapProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [container, setContainer] = useState<HTMLDivElement | null>(null)
  const { theme } = useTheme()

  // Set container after mount
  useEffect(() => {
    if (containerRef.current) {
      setContainer(containerRef.current)
    }
  }, [])

  const { mapRef, liveCount } = useVisitorMap({
    container,
    pmtilesUrl,
    visitors,
    theme,
    hoveredId,
    onVisitorClick: onVisitorSelect,
    onMapReady,
  })

  // Highlight selected visitor
  useEffect(() => {
    const map = mapRef.current
    if (!map || !map.isStyleLoaded()) return

    // Update stroke width based on selection for both live and active markers
    const strokeWidthExpression = [
      'case',
      ['==', ['get', 'id'], selectedId || ''],
      4,
      2,
    ] as maplibregl.ExpressionSpecification

    try {
      map.setPaintProperty('live-markers', 'circle-stroke-width', strokeWidthExpression)
      map.setPaintProperty('active-markers', 'circle-stroke-width', strokeWidthExpression)
    } catch {
      // Layers might not exist yet
    }
  }, [selectedId, mapRef])

  const visitorsWithLocation = visitors.filter(v => v.location)

  // Don't render map if no PMTiles URL
  if (!pmtilesUrl) {
    return (
      <div className="flex-1 flex items-center justify-center bg-[#0d1b2a] light:bg-slate-100 text-muted-foreground">
        PMTiles URL not configured
      </div>
    )
  }

  return (
    <div className="flex-1 relative min-h-0">
      <div ref={containerRef} className="absolute inset-0 w-full h-full" />

      {/* Presence Stats Overlay */}
      <div className="absolute top-4 left-4 bg-[#1b263b]/95 light:bg-white/95 backdrop-blur-sm rounded-lg px-4 py-3 border border-[rgba(127,255,228,0.16)] light:border-emerald-200 light:shadow-md min-w-[160px]">
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-2">
            <span className="size-2 rounded-full bg-[#7fffe4] light:bg-emerald-500 live-pulse" />
            <span className="text-lg font-semibold text-[#7fffe4] light:text-emerald-600">
              {liveCount}
            </span>
            <span className="text-sm text-[#94a3b8] light:text-slate-600">live now</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="size-2 rounded-full bg-[#28d8a1] light:bg-emerald-600 opacity-80" />
            <span className="text-lg font-semibold text-[#28d8a1] light:text-emerald-500">
              {presenceStats.active_count}
            </span>
            <span className="text-sm text-[#94a3b8] light:text-slate-600">active</span>
          </div>
        </div>
      </div>

      {/* Legend - only show when there are visitors with locations */}
      {visitorsWithLocation.length > 0 && (
        <div className="absolute bottom-4 left-4 bg-[#1b263b]/90 light:bg-white/90 backdrop-blur-sm rounded-lg p-3 border border-[rgba(127,255,228,0.16)] light:border-emerald-200 light:shadow-md">
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <span className="size-3 rounded-full bg-[#7fffe4] light:bg-emerald-500 visitor-pulse" />
              <span className="text-[#94a3b8] light:text-slate-600">Live</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="size-3 rounded-full bg-[#7fffe4] light:bg-emerald-500" />
              <span className="text-[#94a3b8] light:text-slate-600">Active</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="size-3 rounded-full bg-[#28d8a1] light:bg-emerald-600 opacity-60" />
              <span className="text-[#94a3b8] light:text-slate-600">Inactive</span>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
